
import React, { useEffect, useState } from "react";
import {
    Button,
    Popover,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Stack,
    Checkbox,
    TextField,
    CircularProgress,
} from "@mui/material";
import { useFormikContext } from "formik";
import { loadEmployees, savePayrollEmployee } from "../api";
import { buildSavePayrollEmployeePayload } from "../Payloads/EntryPayload";
import CustomPagination from "../Components/CustomPagination";
import { enqueueSnackbar } from "notistack";


const AddEmplyeePopover = ({ open, onClose, corporationId, showProfitCenter, prevSelectedEmpls = [], onSuccess }) => {

    const getUniqueKey = (row) => {
        const empId = row?.EmployeeId ?? row?.EmployeeID ?? "";
        const deptId = row?.IncDeptID ?? row?.DepartmentID ?? row?.IncDeptId ?? "";
        const jobId = row?.JobID ?? row?.JobId ?? "";
        const pcId = row?.PCID ?? row?.PcID ?? row?.PCId ?? row?.pcid ?? row?.ProfitCenterID ?? row?.ProfitCenterId ?? "";

        if (showProfitCenter) {
            return `${empId}-${deptId}-${jobId}-${pcId}`;
        }
        return `${empId}-${deptId}-${jobId}`;
    };

    const prevSet = new Set(
        prevSelectedEmpls.map(getUniqueKey)
    );
    const { values } = useFormikContext();

    const [employeeData, setEmployeeData] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);

    const headers = [
        { key: "EmployeeName", label: "Employee" },
        { key: "DepartmentName", label: "Department" },
        { key: "JobTitle", label: "Job" },
    ];
    if (showProfitCenter) headers.push({ key: "PcName", label: "Profit Center" });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const compactTextFieldSx = {
        "& .MuiInputBase-root": {
            height: 30,
            fontSize: "12px",
        },
        "& .MuiInputBase-input": {
            padding: "6px 8px",
            fontSize: "12px",
            fontWeight: 500,
            backgroundColor: "white"
        },
    };

    useEffect(() => {

        fetchEmployeeData();
    }, [page, debouncedSearch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(0);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (open && prevSelectedEmpls.length > 0) {
            // Initialize selection only with previously selected employees when the popover opens.
            // We don't have the full employee objects here, so we store what we have.
            setSelected(prevSelectedEmpls);
        }
    }, [open]);

    const fetchEmployeeData = async () => {

        try {
            setLoading(true)


            const listPayload =
            {
                CorpID: corporationId,
                FrequencyID: values?.frequencyId,
                PageNum: page,
                SerachParam: debouncedSearch || null

            };
            const employeeList = await loadEmployees(listPayload);

            if (employeeList?.StatusCode && employeeList?.StatusCode !== 200) {
                enqueueSnackbar(`${employeeList?.Status}`, { variant: "warning" });
                // setLoading(false);
                return;
            }
            const data = employeeList.LoadEmployees
            // const data = Array.from({ length: 20 }, (_, i) => ({
            //     EmployeeId: `EMP${String(i + 1).padStart(3, "0")}`,
            //     EmployeeName: `Employee ${i + 1}`,
            //     IncDeptID: `DEPT${String(i + 1).padStart(3, "0")}`,
            //     DepartmentName: `Department ${i + 1}`,
            //     JobID: `JOB${String(i + 1).padStart(3, "0")}`,
            //     JobTitle: `Job Title ${i + 1}`,
            //     TotalRecords: 20,
            //     MaxRows: 20
            // }));

            const totalRecords = data?.[0]?.TotalCount || 0;
            const pageSize = data?.[0]?.MaxRows || 0;
            setTotalPages(Math.ceil(totalRecords / pageSize));
            setEmployeeData(data);

            // console.log("employeeListemployeeList", data)
        } catch (error) {
            enqueueSnackbar("Error while Fetch EmployeeData", { variant: "error" })

        } finally {
            setLoading(false);
        }


    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Add all non-disabled items from the current page to the selection
            const newSelections = employeeData.filter(
                (row) => !prevSet.has(getUniqueKey(row))
            );
            const currentKeys = new Set(selected.map(getUniqueKey));
            const uniqueNewSelections = newSelections.filter(
                (row) => !currentKeys.has(getUniqueKey(row))
            );
            setSelected([...selected, ...uniqueNewSelections]);
        } else {
            // Remove all items from the current page from the selection
            const currentPageKeys = new Set(employeeData.map(getUniqueKey));
            setSelected(
                selected.filter((item) => !currentPageKeys.has(getUniqueKey(item)))
            );
        }
    };

    const handleSelectOne = (row) => {
        const rowKey = getUniqueKey(row);

        if (prevSet.has(rowKey)) return;

        setSelected((prev) =>
            prev.some((item) => getUniqueKey(item) === rowKey)
                ? prev.filter((item) => getUniqueKey(item) !== rowKey)
                : [...prev, row]
        );
    };
    const handleApply = async () => {
        try {
            const newSelections = selected.filter(
                (emp) => !prevSet.has(getUniqueKey(emp))
            );
            if (newSelections.length === 0) {
                enqueueSnackbar("Please select at least one employee", { variant: "warning" });
                return;
            }

            const payrollId = values?.payRollID || values?.payrollId || values?.PayrollID || "";

            const applyPayload = buildSavePayrollEmployeePayload(
                payrollId,
                newSelections
            );

            const apply = await savePayrollEmployee(applyPayload);
            if (apply?.StatusCode && apply?.StatusCode !== 200) {
                enqueueSnackbar(`${apply?.Status || apply?.Message || "Failed to add employee(s)"}`, { variant: "warning" });
                return;
            }
            enqueueSnackbar(apply?.Message || apply?.Status || "Employee(s) added successfully", { variant: "success" });
            onSuccess();
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.message || "Error while applying employee(s)", { variant: "error" });
        }
    };


    return (
        <>
            <Popover
                open={Boolean(open)}
                anchorEl={open}
                onClose={onClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                PaperProps={{
                    sx: {
                        width: "60vw",
                        height: "65vh",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        border: "1px solid grey",
                        bgcolor: "#ebf3f9",
                    },
                }}
            >
                <Stack sx={{ height: "100%" }} p={2}>
                    <Stack
                        sx={{
                            height: "30px",
                            mb: 1,
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                        direction={"row"}>
                        <Typography fontSize={13}>
                            ADD EMPLOYEES
                        </Typography>
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <Typography fontSize={12}>
                                Search
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                sx={compactTextFieldSx}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </Stack>

                    </Stack>
                    <TableContainer
                        sx={{
                            // flex: 1,
                            overflow: "auto",
                        }}
                    >
                        <Table size="small" stickyHeader >

                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={{ bgcolor: "#d7d7d7" }}>
                                        <Checkbox
                                            size="small"
                                            color="grey"
                                            // checked={
                                            //     employeeData.length > 0 &&
                                            //     employeeData.every((row) =>
                                            //         selected.some(item => item.EmployeeId === row.EmployeeId)
                                            //     )
                                            // }
                                            // indeterminate={
                                            //     employeeData.some((row) =>
                                            //         selected.some(item => item.EmployeeId === row.EmployeeId)
                                            //     ) &&
                                            //     !employeeData.every((row) =>
                                            //         selected.some(item => item.EmployeeId === row.EmployeeId)
                                            //     )
                                            // }
                                            checked={
                                                employeeData.length > 0 &&
                                                employeeData.every((row) =>
                                                    selected.some(
                                                        (item) => getUniqueKey(item) === getUniqueKey(row)
                                                    ) || prevSet.has(getUniqueKey(row))
                                                )
                                            }
                                            indeterminate={
                                                employeeData.some((row) =>
                                                    selected.some(
                                                        (item) => getUniqueKey(item) === getUniqueKey(row)
                                                    )
                                                ) &&
                                                !employeeData.every((row) =>
                                                    selected.some(
                                                        (item) => getUniqueKey(item) === getUniqueKey(row)
                                                    ) || prevSet.has(getUniqueKey(row))
                                                )
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    {headers.map((item) => (
                                        <TableCell
                                            key={item.key}
                                            sx={{
                                                borderRight: "1px solid white",
                                                bgcolor: "#d7d7d7",
                                                fontSize: 13
                                            }}
                                        >
                                            {item.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {
                                    loading ?
                                        <TableRow>
                                            <TableCell
                                                colSpan={headers.length + 1}
                                                align="center"
                                            >
                                                <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"center"}>
                                                    <CircularProgress size={15} />
                                                    <span>Loading...</span>

                                                </Stack>

                                            </TableCell>
                                        </TableRow>
                                        :
                                        employeeData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ color: "gray" }}>
                                                    No Data Found
                                                </TableCell>
                                            </TableRow>
                                        ) : (

                                            employeeData.map((row, index) => {
                                                // const isSelected = selected.some(
                                                //     (item) => item.EmployeeId === row.EmployeeId
                                                // );
                                                const isSelected = selected.some(
                                                    (item) => getUniqueKey(item) === getUniqueKey(row)
                                                );

                                                return (
                                                    <TableRow
                                                        key={getUniqueKey(row) || index}
                                                        hover
                                                        selected={isSelected}


                                                    >
                                                        <TableCell padding="checkbox" sx={{ bgcolor: "white" }}>
                                                            <Checkbox
                                                                size="small"
                                                                color="grey"
                                                                // checked={isSelected || prevSet.has(row.EmployeeId)}
                                                                // disabled={prevSet.has(row.EmployeeId)}
                                                                checked={isSelected || prevSet.has(getUniqueKey(row))}
                                                                disabled={prevSet.has(getUniqueKey(row))}
                                                                onChange={() => handleSelectOne(row)}
                                                                sx={{
                                                                    opacity: prevSet.has(getUniqueKey(row)) ? 0.5 : 1
                                                                }}
                                                            />
                                                        </TableCell>

                                                        {headers.map((col) => (
                                                            <TableCell sx={{
                                                                backgroundColor: "white", fontSize: 12,
                                                                fontWeight: 500,
                                                                color: "#626262",
                                                            }}
                                                                key={col.key}
                                                            >
                                                                {row[col.key]}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            })

                                        )}
                            </TableBody>

                        </Table>
                    </TableContainer>
                    <Box width={"100%"} mt={"2px"} flex={1}>
                        <CustomPagination
                            count={totalPages}
                            page={page}
                            onChange={(e, newPage) => setPage(newPage - 1)}
                        />
                    </Box>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1}
                        sx={{ mt: 1, flexShrink: 0 }}
                    >
                        <Button
                            size="small"
                            // onClick={() => {
                            //     setFieldValue("employees", selected);
                            //     onClose();
                            // }}
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                        <Button size="small" onClick={onClose}>Cancel</Button>
                    </Stack>

                </Stack>
            </Popover>

        </>
    )
};

export default AddEmplyeePopover;